import { useMemo, useState } from 'react';

import { Checkbox, FormControlLabel, FormGroup, Grid, Radio, Stack, Typography } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid-pro';
import { FormikProvider, getIn, useFormik } from 'formik';
import * as Yup from 'yup';

import { EstadoAutoComplete, TipoLojaAutoComplete } from 'components/autocompletes';
import { RoyaltiesAutoComplete } from 'components/autocompletes/RoyaltiesAutoComplete';
import { DownloadModeloArquivoBtn } from 'components/buttons/DownloadModeloArquivoBtn';
import { BbarForm } from 'components/docked/BbarForm';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useLoaderContext } from 'context/LoaderContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import {
    ConfigPedidoHeaderActions,
    ConfigPedidoImportacaoUploadBtn,
    ConfigPedidoLojasGrid,
    ConfigPedidoLojasModal,
} from 'pages/compra/configuracao-pedido/components';
import { ConfigPedidoListProvider, useConfigPedidoStepContext } from 'pages/compra/configuracao-pedido/contexts';
import { useConfigPedidoDirty, useMutateCondicoesVisualizacao, useQueryCondicoesVisualizacao } from 'pages/compra/configuracao-pedido/hooks';
import { EstadosProps, initialCondicoesVisualizacaoValues, LojasProps } from 'pages/compra/configuracao-pedido/types';

export function ConfigPedidoCondicoesVisualizacaoForm() {
    const { data, isFetching } = useQueryCondicoesVisualizacao();
    const { onSubmit, isLoading } = useMutateCondicoesVisualizacao(data || initialCondicoesVisualizacaoValues);

    const { setActiveStepDirty } = useConfigPedidoStepContext();
    const { focusError } = useFocusErrorFormik();

    const [openLojasModal, setOpenLojasModal] = useState<boolean>(false);

    useLoaderContext(isFetching || isLoading);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                configPedidoFaixaRoyalties: Yup.object().when('fgLojaFaixaRoyalties', {
                    is: true,
                    then: (schema) => schema.required(),
                    otherwise: (schema) => schema.nullable(),
                }),
            }),
        [],
    );

    const formik = useFormik({
        initialValues: data || initialCondicoesVisualizacaoValues,
        onSubmit,
        enableReinitialize: true,
        validationSchema,
    });

    const { errors, setFieldValue, values, dirty, submitForm } = formik;

    focusError(errors);

    useConfigPedidoDirty(dirty);

    /**
     * Adiciona as lojas selecionadas na lista de lojas do pedido.
     */
    const addLojas = (lojas: LojasProps[]) => {
        const currentLojasList = values.configPedidoLojas || [];

        const newLojas = lojas.filter((loja) => {
            const lojaInList = currentLojasList.find((currentLoja) => currentLoja.loja.idLoja === loja.idLoja);

            return !lojaInList || lojaInList.fgAtivo === false;
        });

        const mappedNewLojas = newLojas.map((loja) => ({
            loja,
            fgAtivo: true,
            configPedido: { idConfigPedido: values.idConfigPedido || 0 },
            peRoyalties: loja.peRoyalties,
            vlPedidoMinimo: loja.vlPedidoMinimo,
            tipoControlePedidoMinimo: loja.tipoControlePedidoMinimo,
        }));

        setFieldValue('configPedidoLojas', [...currentLojasList, ...mappedNewLojas]);
    };

    /**
     * Remove as lojas selecionadas da lista de lojas do pedido.
     */
    const removeLojas = (idList: Set<GridRowId>) => {
        setFieldValue(
            'configPedidoLojas',
            values.configPedidoLojas.filter((loja) => !idList.has(loja.loja.idLoja)),
        );
    };

    return (
        <FormikProvider value={formik}>
            <ConfigPedidoLojasModal open={openLojasModal} setOpen={setOpenLojasModal} handleAdicionarLojas={addLojas} />

            <Block>
                <SubTitle label="Segmentação" />

                <Stack>
                    <Typography>A configuração do pedido será disponibilizado para:</Typography>

                    <FormGroup row sx={{ pt: 1, pb: 2 }}>
                        <FormControlLabel
                            label="Lojas"
                            control={<Radio />}
                            checked={values.tpDisponibilizacao === 'lojas'}
                            onChange={() => setFieldValue('tpDisponibilizacao', 'lojas')}
                            value="lojas"
                        />

                        <FormControlLabel
                            label="Estados"
                            control={<Radio />}
                            checked={values.tpDisponibilizacao === 'estados'}
                            onChange={() => setFieldValue('tpDisponibilizacao', 'estados')}
                            value="estados"
                        />
                    </FormGroup>
                </Stack>

                {values.tpDisponibilizacao === 'lojas' && (
                    <Grid container sx={{ justifyContent: 'space-between', gap: 2, width: '100%' }}>
                        <Grid>
                            <ConfigPedidoImportacaoUploadBtn
                                url="/gestao/configpedidoloja/upload"
                                onSend={() => setActiveStepDirty()}
                                onSuccessUploadFile={(responseData) => {
                                    setFieldValue('configPedidoLojas', responseData);
                                }}
                            />
                        </Grid>

                        <Grid>
                            <DownloadModeloArquivoBtn dsChave="URL_ARQUIVO_MODELO_LOJAS_CONFIG_PEDIDO" />
                        </Grid>
                    </Grid>
                )}

                {values.tpDisponibilizacao === 'estados' && (
                    <EstadoAutoComplete
                        multiple
                        size="small"
                        name="configPedidoEstados"
                        disabled={false}
                        error={getIn(errors, 'configPedidoEstados')}
                        value={values.configPedidoEstados?.map((e) => e.estado)}
                        onChange={(e, value: EstadosProps[]) => {
                            const newEstados = value.map((estado) => ({
                                estado,
                                fgAtivo: true,
                                configPedido: { idConfigPedido: values.idConfigPedido || 0 },
                            }));

                            setFieldValue('configPedidoEstados', newEstados);
                        }}
                    />
                )}
            </Block>

            {values.tpDisponibilizacao === 'lojas' && (
                <ConfigPedidoListProvider onAdd={() => setOpenLojasModal(true)} onRemove={removeLojas} searchPlaceholder="Nome da loja...">
                    <Block>
                        <SubTitle label="Lojas" />

                        <ConfigPedidoHeaderActions />

                        <ConfigPedidoLojasGrid configPedidoLojas={values.configPedidoLojas ?? []} setFieldValue={setFieldValue} />
                    </Block>
                </ConfigPedidoListProvider>
            )}

            <Block>
                <SubTitle label="Condições" />

                <TipoLojaAutoComplete
                    name="tipoLojaVisualizar"
                    label="Somente lojas"
                    value={values.tipoLojaVisualizar || null}
                    onChange={(e, value) => setFieldValue('tipoLojaVisualizar', value)}
                    error={errors.tipoLojaVisualizar}
                    sx={{ width: { xs: '100%', md: 170 }, mb: 1 }}
                    size="small"
                />

                <FormGroup>
                    <FormControlLabel
                        label="Somente lojas com meta atingida"
                        name="fgLojaMetaAtingida"
                        control={<Checkbox />}
                        checked={values.fgLojaMetaAtingida}
                        onChange={(e, checked) => setFieldValue('fgLojaMetaAtingida', checked)}
                    />

                    <Grid container sx={{ gap: 1, width: '100%' }}>
                        <Grid>
                            <FormControlLabel
                                label="Somente lojas com % de royalties igual à:"
                                name="fgLojaFaixaRoyalties"
                                control={<Checkbox />}
                                checked={values.fgLojaFaixaRoyalties}
                                onChange={(e, checked) => setFieldValue('fgLojaFaixaRoyalties', checked)}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 'grow' }}>
                            <RoyaltiesAutoComplete
                                name="configPedidoFaixaRoyalties"
                                value={values.configPedidoFaixaRoyalties}
                                error={errors.configPedidoFaixaRoyalties}
                                onChange={(e, value) => setFieldValue('configPedidoFaixaRoyalties', value)}
                                size="small"
                                disabled={!values.fgLojaFaixaRoyalties}
                                sx={{ width: { md: 144, xs: 'auto' } }}
                            />
                        </Grid>
                    </Grid>
                </FormGroup>
            </Block>

            <BbarForm submitForm={submitForm} />
        </FormikProvider>
    );
}
